import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { clearSuccess } from '~/actions/success';
import { useIsSuccess } from '~/hooks';
import {
  ADD_PAYMENT_ACTION,
  DELETE_DEFAULT_PAYMENT_ACTION,
  SELECT_PAYMENT_ACTION,
  SUBMIT_PLAID_TOKEN,
} from '../../../actions';
import { PostingStatus, useGetPostings } from '../../../domain/Postings';
import { useModal } from '../../ModalQueue';
import { ModalOrder } from '../../ModalQueue/modalOrder';
import { useRetryBillingPayment } from './useRetryBillingPayment';

const actions = [
  SELECT_PAYMENT_ACTION,
  SUBMIT_PLAID_TOKEN,
  ADD_PAYMENT_ACTION,
  DELETE_DEFAULT_PAYMENT_ACTION,
];

export const useSuspendedPostingsPopup = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const { isActive: isModalActive, hide: hideModal } = useModal({
    id: ModalOrder.SUSPENDED_POSTINGS_POPUP,
    condition: isOpen,
  });

  const { isSuccess: selectPaymentSuccess } = useIsSuccess(actions);

  const {
    data: postings,
    pagination,
    isLoading,
    refetch,
  } = useGetPostings(
    {
      page: 1,
      limit: 15,
      status: PostingStatus.SUSPENDED,
    },
    { enabled: false },
  );

  const totalCount = pagination?.total || 0;

  const {
    paymentResultState,
    isProcessing,
    failedPostingIds,
    handleRetryBilling,
    resetPaymentState,
  } = useRetryBillingPayment();

  useEffect(() => {
    // When payment method is selected successfully, fetch and show popup
    if (selectPaymentSuccess && !isOpen) {
      refetch().then((result) => {
        const hasPostings = (result.data?.data?.length ?? 0) > 0;
        if (hasPostings) {
          setIsOpen(true);
        }
      });
      dispatch(clearSuccess(actions));
    }
  }, [selectPaymentSuccess, isOpen, refetch, dispatch]);

  const failedPostings = useMemo(() => {
    if (failedPostingIds.length === 0) {
      return [];
    }
    return postings.filter((posting) => failedPostingIds.includes(posting.id));
  }, [postings, failedPostingIds]);

  const handlePayNow = async () => {
    const result = await handleRetryBilling();
    if (result) {
      setIsOpen(false);
      hideModal();
    }
  };

  const handlePayLater = () => {
    setIsOpen(false);
    hideModal();
  };

  const handleCloseSuccessPopup = () => {
    resetPaymentState();
    refetch();
  };

  const handleRetryFromFailedPopup = () => {
    resetPaymentState();
    handlePayNow();
  };

  const handlePayLaterFromFailedPopup = () => {
    resetPaymentState();
  };

  return {
    isOpen: isModalActive,
    suspendedPostings: postings,
    failedPostings,
    totalCount,
    isLoading,
    isProcessing,
    paymentResultState,
    handlePayNow,
    handlePayLater,
    handleCloseSuccessPopup,
    handleRetryFromFailedPopup,
    handlePayLaterFromFailedPopup,
  };
};
